import React from 'react'
import { NewAppLayout } from '@/components/new-ux/layout/NewAppLayout'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { isPrimaryDomain } from '@/lib/primary-domains'
import { PRIMARY_DOMAINS } from '@/lib/primary-domains'
import {
  Globe,
  Mail,
  Linkedin,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Zap,
  Activity,
  Database,
  Shield
} from 'lucide-react'
import { useMailboxes } from "@/hooks/useMailboxes"
import { useDomains } from "@/hooks/useDomains"
import { useLinkedInProfiles } from "@/hooks/useLinkedInProfiles"
import { useMissingDomains } from "@/hooks/useMissingDomains"
import { useWorkspaceClient } from "@/hooks/useWorkspaceClient"

const SendingInfrastructure = () => {
  const workspace = useWorkspaceClient()
  const { data: mailboxes = [] } = useMailboxes(workspace.mailboxClientId)
  const { data: domains = [] } = useDomains()
  const { data: linkedInProfiles = [] } = useLinkedInProfiles()
  const { data: missingDomains = [] } = useMissingDomains()

  // Missing domains metrics
  const totalMissingDomains = missingDomains.length
  const totalMissingMailboxes = missingDomains.reduce((sum, d) => sum + d.mailbox_count, 0)

  // Mailbox provider breakdown from mailboxes table (source of truth)
  const domainProviderMap = new Map(domains.map(d => [d.domain?.toLowerCase(), d.mailbox_provider]))
  const activeMailboxesList = mailboxes.filter(m => m.status !== 'DEACTIVATED')
  const cheapinboxesActiveMailboxes = activeMailboxesList.filter(m => {
    const provider = domainProviderMap.get(m.domain?.toLowerCase())
    return provider === 'Cheapinboxes'
  }).length
  const inboxkitActiveMailboxes = activeMailboxesList.filter(m => {
    const provider = domainProviderMap.get(m.domain?.toLowerCase())
    return provider === 'InboxKit'
  }).length

  // Domain calculations
  const activeDomains = domains.filter(d => d.status === 'Active')
  const nonActiveDomains = domains.filter(d => d.status !== 'Active')
  const domainsWithoutMailboxes = domains.filter(d => d.status === 'Active' && d.total_mailboxes === 0)

  // Primary domains count (only Active ones)
  const allPrimaryDomains = domains.filter(d => {
    const domainName = d.domain?.toLowerCase()
    return domainName && PRIMARY_DOMAINS.includes(domainName as any)
  })
  const primaryDomainsCount = allPrimaryDomains.filter(d => d.status === 'Active').length
  const deactivatedPrimaryCount = allPrimaryDomains.filter(d => d.status !== 'Active').length

  const expiringDomains = domains.filter(d => {
    if (!d.expiry_date) return false
    if (d.status !== 'Active') return false
    const expiryDate = new Date(d.expiry_date)
    const thirtyDaysFromNow = new Date()
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)
    return expiryDate <= thirtyDaysFromNow && expiryDate >= new Date()
  })

  // Mailbox calculations — use smartlead_id as connected indicator (consistent with MailboxMetrics)
  const activeMailboxes = mailboxes.filter(m => m.status !== 'DEACTIVATED').length
  const deactivatedMailboxes = mailboxes.filter(m => m.status === 'DEACTIVATED').length
  const connectedToSmartlead = mailboxes.filter(m => m.smartlead_id != null && m.status !== 'DEACTIVATED').length
  const connectedToMasterinbox = mailboxes.filter(m => (m as any).masterinbox_connected === true && m.status !== 'DEACTIVATED').length

  const dailyCapacity = mailboxes
    .filter(m => m.smartlead_id != null && m.status !== 'DEACTIVATED')
    .reduce((sum, m) => sum + (m.message_per_day || 0), 0)

  // Use smartlead_id for connected mailboxes (consistent with MailboxMetrics)
  const successfulSyncMailboxes = mailboxes.filter(m => m.smartlead_id != null && m.status !== 'DEACTIVATED')

  // Warmup metrics (consistent with MailboxMetrics)
  const warmupActive = successfulSyncMailboxes.filter(m => m.warmup_status === 'ACTIVE').length
  const warmupInactive = successfulSyncMailboxes.filter(m => {
    if (isPrimaryDomain(m.email)) return false
    if (m.status === 'DEACTIVATED') return false
    return m.warmup_status === 'INACTIVE'
  }).length
  const warmupNull = successfulSyncMailboxes.filter(m => {
    if (isPrimaryDomain(m.email)) return false
    return m.warmup_status === null || m.warmup_status === undefined
  }).length
  const primaryMailboxes = successfulSyncMailboxes.filter(m => isPrimaryDomain(m.email)).length

  return (
    <NewAppLayout fullWidth>
      <div className="space-y-8">

            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Domains</CardTitle>
                  <Globe className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{activeDomains.length}</div>
                  <p className="text-xs text-muted-foreground">{domains.length} total domains</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Mailboxes</CardTitle>
                  <Mail className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{activeMailboxes}</div>
                  <p className="text-xs text-muted-foreground">{connectedToSmartlead} SmartLead connected</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">LinkedIn Profiles</CardTitle>
                  <Linkedin className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{linkedInProfiles.length}</div>
                  <p className="text-xs text-muted-foreground">{linkedInProfiles.filter(p => p.platform_status === 'active').length} active profiles</p>
                </CardContent>
              </Card>
            </div>

            {/* Domain Infrastructure */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Globe className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-semibold">Domain Infrastructure</CardTitle>
                </div>
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <Activity className="h-3 w-3 mr-1" />
                  Live
                </Badge>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Headline Numbers */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-5 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">{activeDomains.length}</div>
                      </div>
                      <div className="text-sm font-semibold text-foreground">Active Domains</div>
                      <div className="text-xs text-muted-foreground mt-1">{activeDomains.filter(d => d.domain_registrar === 'Spaceship').length} Spaceship · {activeDomains.filter(d => d.domain_registrar === 'InboxKit').length} InboxKit</div>
                    </div>
                  </div>
                  <div className="p-5 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Mail className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">{activeMailboxes}</div>
                      </div>
                      <div className="text-sm font-semibold text-foreground">Active Mailboxes</div>
                      <div className="text-xs text-muted-foreground mt-1">{cheapinboxesActiveMailboxes} Cheapinboxes · {inboxkitActiveMailboxes} InboxKit</div>
                    </div>
                  </div>
                </div>

                {/* Domain Details */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30">
                      <Globe className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground">Domain Details</h3>
                  </div>
                  <div className="grid grid-cols-5 gap-4">
                    <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30">
                            <Shield className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="text-3xl font-bold text-foreground">{primaryDomainsCount}</div>
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">Primary Domains</div>
                        <div className="text-xs text-muted-foreground/70 mt-1">
                          {deactivatedPrimaryCount > 0
                            ? `${deactivatedPrimaryCount} deactivated`
                            : 'Business domains'}
                        </div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30">
                            <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div className="text-3xl font-bold text-foreground">{domainsWithoutMailboxes.length}</div>
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">No Mailboxes</div>
                        <div className="text-xs text-muted-foreground/70 mt-1">Active domains</div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div className="p-1.5 rounded-md bg-rose-100 dark:bg-rose-900/30">
                            <XCircle className="h-3 w-3 text-rose-600 dark:text-rose-400" />
                          </div>
                          <div className="text-3xl font-bold text-foreground">{nonActiveDomains.length}</div>
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">Deactivated</div>
                        <div className="text-xs text-muted-foreground/70 mt-1">{domains.length} total</div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30">
                            <Clock className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div className="text-3xl font-bold text-foreground">{expiringDomains.length}</div>
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">Expiring</div>
                        <div className="text-xs text-muted-foreground/70 mt-1">In next 30 days</div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div className="p-1.5 rounded-md bg-slate-100 dark:bg-slate-900/30">
                            <Database className="h-3 w-3 text-slate-600 dark:text-slate-400" />
                          </div>
                          <div className="text-3xl font-bold text-foreground">{totalMissingDomains}</div>
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">Missing</div>
                        <div className="text-xs text-muted-foreground/70 mt-1">{totalMissingMailboxes} untracked</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Infrastructure */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-semibold">Email Infrastructure</CardTitle>
                </div>
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <Activity className="h-3 w-3 mr-1" />
                  Live
                </Badge>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Headline Numbers */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-5 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">{activeMailboxes}</div>
                      </div>
                      <div className="text-sm font-semibold text-foreground">Active Mailboxes</div>
                      <div className="text-xs text-muted-foreground mt-1">{deactivatedMailboxes} deactivated · {activeMailboxes + deactivatedMailboxes} total</div>
                    </div>
                  </div>
                  <div className="p-5 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">{connectedToSmartlead}</div>
                      </div>
                      <div className="text-sm font-semibold text-foreground">Connected to SmartLead</div>
                      <div className="text-xs text-muted-foreground mt-1">{activeMailboxes > 0 ? Math.round((connectedToSmartlead / activeMailboxes) * 100) : 0}% of active · {dailyCapacity.toLocaleString()} emails/day</div>
                    </div>
                  </div>
                  <div className="p-5 rounded-lg bg-purple-50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        <div className="text-4xl font-bold text-purple-600 dark:text-purple-400">{connectedToMasterinbox}</div>
                      </div>
                      <div className="text-sm font-semibold text-foreground">Connected to MasterInbox</div>
                      <div className="text-xs text-muted-foreground mt-1">{activeMailboxes > 0 ? Math.round((connectedToMasterinbox / activeMailboxes) * 100) : 0}% of active</div>
                    </div>
                  </div>
                </div>

                {/* Warmup Details */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30">
                      <Zap className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground">Warmup Details</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div className="p-1.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30">
                            <Activity className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="text-3xl font-bold text-foreground">{warmupActive}</div>
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">Warming Up</div>
                        <div className="text-xs text-muted-foreground/70 mt-1">{connectedToSmartlead > 0 ? Math.round((warmupActive / connectedToSmartlead) * 100) : 0}% of connected</div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30">
                            <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div className="text-3xl font-bold text-foreground">{warmupInactive + warmupNull}</div>
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">Warmup Inactive</div>
                        <div className="text-xs text-muted-foreground/70 mt-1">Paused + No status</div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30">
                            <Globe className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="text-3xl font-bold text-foreground">{primaryMailboxes}</div>
                        </div>
                        <div className="text-sm font-medium text-muted-foreground">Primary Mailboxes</div>
                        <div className="text-xs text-muted-foreground/70 mt-1">Business domain mailboxes</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

      </div>
    </NewAppLayout>
  )
}

export default SendingInfrastructure
